import { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Grid,
  Alert
} from '@mui/material';
import { LeadFormSchema, type LeadFormInput } from '@azoupvendas/shared';

// AIDEV-NOTE: Formulario de criacao/edicao de Lead com validacao Zod via React Hook Form

interface LeadFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: LeadFormInput) => Promise<void>;
  defaultValues?: Partial<LeadFormInput>;
  title?: string;
}

const ETAPAS = [
  { value: 'novo', label: 'Novo' },
  { value: 'qualificado', label: 'Qualificado' },
  { value: 'proposta', label: 'Proposta' },
  { value: 'ganho', label: 'Ganho' },
  { value: 'perdido', label: 'Perdido' }
];

export function LeadForm({
  open,
  onClose,
  onSubmit,
  defaultValues,
  title = 'Novo Lead'
}: LeadFormProps) {
  const [submitError, setSubmitError] = useState<string | null>(null);
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<LeadFormInput>({
    resolver: zodResolver(LeadFormSchema),
    defaultValues: { nome: '', empresa: '', estagio: 'novo', ...defaultValues }
  });

  const handleClose = () => {
    reset();
    setSubmitError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <form
        onSubmit={handleSubmit(async (data) => {
          try {
            setSubmitError(null);
            await onSubmit(data);
            handleClose();
          } catch (err) {
            setSubmitError(err instanceof Error ? err.message : 'Erro ao salvar. Tente novamente.');
          }
        })}
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent>
          {submitError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {submitError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Controller
                name="nome"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Nome *"
                    fullWidth
                    error={!!errors.nome}
                    helperText={errors.nome?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="empresa"
                control={control}
                render={({ field }) => <TextField {...field} label="Empresa" fullWidth />}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="estagio"
                control={control}
                render={({ field }) => (
                  <TextField {...field} select label="Etapa *" fullWidth error={!!errors.estagio}>
                    {ETAPAS.map((e) => (
                      <MenuItem key={e.value} value={e.value}>
                        {e.label}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="valor_estimado"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Valor estimado (R$)"
                    type="number"
                    fullWidth
                    onChange={(e) =>
                      field.onChange(e.target.value ? Number(e.target.value) : undefined)
                    }
                    error={!!errors.valor_estimado}
                    helperText={errors.valor_estimado?.message}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Controller
                name="probabilidade"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Probabilidade (%)"
                    type="number"
                    fullWidth
                    onChange={(e) =>
                      field.onChange(e.target.value ? Number(e.target.value) : undefined)
                    }
                    error={!!errors.probabilidade}
                    helperText={errors.probabilidade?.message}
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button type="submit" variant="contained" disabled={isSubmitting}>
            {isSubmitting ? 'Salvando...' : 'Salvar'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
